import GameState, { PlayerMoves } from "../state/gameState";
import { checkPlayerAction } from "../state/checkPlayerAction";
import Phaser from "phaser";
import TurnBasedInputPlugin from "../plugins/TurnBasedInputPlugin";
import anims from "../artConfig.json";
import { GAME_VARIABLES, GameEvents, layerDepthDiff } from "../constants";
import { RemovedBecause } from "../state/rules/overlap";
import { SoundPlayer } from "../sounds/SoundPlayer";
import { GlobalEvents, EGlobalEvent } from "../plugins/GlobalGameEvents";

// Do not change representation. We use this for animations
export enum Dir {
  Left = 1,
  Right = 2,
  Up = 3,
  Down = 4,
}

const REVESE_ANIMATION_DELAY = 80;

function getPlayerFromState(state: any) {
  return state.player;
}

export default class Player {
  private state: GameState;
  public sprite: Phaser.GameObjects.Sprite;
  private input: TurnBasedInputPlugin;
  private scene: Phaser.Scene;
  public died: boolean;
  constructor(gameState: GameState, scene: Phaser.Scene) {
    this.state = gameState;
    this.scene = scene;
    const starting_direction = Dir.Right;
    this.sprite = scene.add.sprite(
      0,
      0,
      "atlas",
      `Player/Player${starting_direction}`
    );
    this.died = false;
    this.input = (scene as any).myInput;
    scene.events.on("update", this.update, this);
    scene.events.on(GameEvents.UNDO_Pressed, this.undo, this);
    scene.events.on(GameEvents.PlayerDied, this.onDied, this);
    scene.events.once("destroy", () => {
      scene.events.removeListener(GameEvents.PlayerDied, this.onDied, this);
      scene.events.removeListener("update", this.update, this);
      scene.events.removeListener(GameEvents.UNDO_Pressed, this.undo, this);
    });
    this.undo();

    const particles = scene.add
      .particles("atlas", "FX/particle_shapes/particle_shapes2")
      .setDepth(100);

    const emitter = particles
      .createEmitter({
        follow: this.sprite,
        followOffset: { x: 4, y: 6 },
        frequency: 50,
        lifespan: 200,
        speedX: [-0.1, 0.1],
        speedY: [0.1, -0.1],
      })
      .setTint(0xa6603a)
      .setAlpha(function (
        p: Phaser.GameObjects.Particles.Particle,
        _k: number,
        t: number
      ) {
        return 1 - 2 * Math.abs(t - 0.5);
      })
      .onParticleEmit(() => {
        particles.setDepth(this.sprite.depth - 0.01);
        particles.setVisible(!this.died);
      });

    GlobalEvents.once(EGlobalEvent.WonLevel, () => {
      emitter.setFrequency(-1);
      emitter.setVisible(false);
    });
  }

  undo() {
    this.died = false;
    this.scene.tweens.killTweensOf(this.sprite);
    let state = getPlayerFromState(this.state.gameState);

    this.sprite.alpha = 1;
    this.sprite.x = state.x * 8;
    this.sprite.y = state.y * 8;
    this.sprite.setTexture("atlas", `Player/Player${state.dir}`);
    this.setDepth();
  }

  setDepth() {
    let state = getPlayerFromState(this.state.gameState);
    if (state.dir === Dir.Down) {
      this.sprite.setDepth(state.y + 1 + layerDepthDiff.PLAYER);
    } else {
      this.sprite.setDepth(state.y + layerDepthDiff.PLAYER);
    }
  }

  update() {
    if (!this.died && getPlayerFromState(this.state.gameState)) {
      this.sprite.setVisible(true);
      this.setDepth();
      this.handleInputs();
    }
  }

  addBounce(time: number) {
    const moveY = getPlayerFromState(this.state.gameState).y * 8;
    this.scene.tweens.add({
      targets: this.sprite,
      y: { from: moveY, to: moveY - 2 },
      yoyo: true,
      repeat: 0,
      duration: time,
      ease: "Bounce.easeInOut",
    });
  }

  onDied({ reason }: { reason: RemovedBecause }) {
    let initDepth = 0;

    this.state.mutateGameState((state) => {
      if (state.player) {
        initDepth = state.player.y + 0.1;
      }
      delete state.player;
    });
    this.died = true;
    if (
      reason === RemovedBecause.Fallen &&
      (this as any).sprite !== undefined
    ) {
      const sprite: Phaser.GameObjects.Sprite = (this as any).sprite as any;
      this.scene.time.delayedCall(GAME_VARIABLES.MOVE_TIME_MS - 2, () => {
        const initialAlpha = sprite.alpha;
        this.sprite.setDepth(initDepth - 1);
        this.scene.add.tween({
          targets: sprite,
          y: "+= 34",
          alpha: { from: initialAlpha, to: 0 },
          ease: "Quad.easeIn",
          duration: 1000,
          onComplete: () => {
            sprite.setVisible(false);
          },
        });
      });
    } else {
      this.scene.time.delayedCall(GAME_VARIABLES.MOVE_TIME_MS - 2, () => {
        this.scene.add.tween({
          targets: this.sprite,
          alpha: { from: 1, to: 0 },
          repeat: 3,
          duration: 50,
          repeatDelay: 50,
        });
      });
    }
  }

  failedMove() {
    const y = getPlayerFromState(this.state.gameState).y * 8;
    this.scene.tweens.add({
      targets: this.sprite,
      y: { from: y, to: y - 1 },
      duration: GAME_VARIABLES.MOVE_TIME_MS,
      yoyo: true,
      repeat: 0,
    });
  }

  // Called at the start of the update..
  handleInputs() {
    let { dir, x, y }: { dir: Dir; x: number; y: number } = getPlayerFromState(
      this.state.gameState
    );
    if (this.input.isJustPressedAction) {
      if (dir === Dir.Right) {
        this.state.addBulletFromPool(x + 2, y, dir);
      }
      if (dir === Dir.Left) {
        this.state.addBulletFromPool(x - 2, y, dir);
      }
      if (dir === Dir.Up) {
        this.state.addBulletFromPool(x, y - 2, dir);
      }
      if (dir === Dir.Down) {
        this.state.addBulletFromPool(x, y + 2, dir);
      }
      SoundPlayer.playerShootSFX(this.scene);
      this.scene.cameras.main.shake(
        GAME_VARIABLES.MOVE_TIME_MS * 2,
        0.005,
        true
      );
      this.scene.events.emit(GameEvents.PlayerMadeTurn);
    }
    if (this.input.isJustPressedDown && [Dir.Down, Dir.Up].includes(dir)) {
      if (
        !checkPlayerAction(this.state, PlayerMoves.MoveDown, this.scene.events)
      ) {
        this.failedMove();
        return;
      }
      SoundPlayer.playerMoveSFX(this.scene);
      this.state.mutateGameState((state) => {
        getPlayerFromState(state).y += 1;
      });
      const move = getPlayerFromState(this.state.gameState).y * 8;
      this.scene.tweens.add({
        targets: this.sprite,
        y: { from: move, to: move + 8 },
        duration: GAME_VARIABLES.MOVE_TIME_MS,
        repeat: 0,
        ease: "Back.easeIn",
      });
    } else if (
      this.input.isJustPressedLeft &&
      [Dir.Left, Dir.Right].includes(dir)
    ) {
      if (
        !checkPlayerAction(this.state, PlayerMoves.MoveLeft, this.scene.events)
      ) {
        this.failedMove();
        return;
      }
      SoundPlayer.playerMoveSFX(this.scene);
      this.state.mutateGameState((state) => {
        getPlayerFromState(state).x -= 1;
      });
      const move = getPlayerFromState(this.state.gameState).x * 8;
      this.scene.tweens.add({
        targets: this.sprite,
        x: { from: move, to: move - 8 },
        duration: GAME_VARIABLES.MOVE_TIME_MS,
        repeat: 0,
      });
      this.addBounce(GAME_VARIABLES.MOVE_TIME_MS);
    } else if (
      this.input.isJustPressedRight &&
      [Dir.Left, Dir.Right].includes(dir)
    ) {
      if (
        !checkPlayerAction(this.state, PlayerMoves.MoveRight, this.scene.events)
      ) {
        this.failedMove();
        return;
      }
      SoundPlayer.playerMoveSFX(this.scene);
      this.state.mutateGameState((state) => {
        getPlayerFromState(state).x += 1;
      });
      const move = getPlayerFromState(this.state.gameState).x * 8;
      this.scene.tweens.add({
        targets: this.sprite,
        x: { from: move, to: move + 8 },
        duration: GAME_VARIABLES.MOVE_TIME_MS,
        repeat: 0,
      });
      this.addBounce(GAME_VARIABLES.MOVE_TIME_MS);
    } else if (this.input.isJustPressedUp && [Dir.Down, Dir.Up].includes(dir)) {
      if (
        !checkPlayerAction(this.state, PlayerMoves.MoveUp, this.scene.events)
      ) {
        this.failedMove();
        return;
      }
      SoundPlayer.playerMoveSFX(this.scene);
      this.state.mutateGameState((state) => {
        getPlayerFromState(state).y -= 1;
      });
      const move = getPlayerFromState(this.state.gameState).y * 8;
      this.scene.tweens.add({
        targets: this.sprite,
        y: { from: move, to: move - 8 },
        duration: GAME_VARIABLES.MOVE_TIME_MS,
        repeat: 0,
        ease: "Back.easeOut",
      });
    } else if (
      this.input.isJustPressedDown &&
      [Dir.Left, Dir.Right].includes(dir)
    ) {
      SoundPlayer.playerTurnSFX(this.scene);
      if (dir === Dir.Left) {
        this.sprite.anims.playReverse(anims.animations.playerLeftDown.key);
      } else {
        this.sprite.anims.play(anims.animations.playerRightDown.key);
      }
      if (
        checkPlayerAction(this.state, PlayerMoves.FaceDown, this.scene.events)
      ) {
        this.state.mutateGameState((state) => {
          getPlayerFromState(state).dir = Dir.Down;
        });
      } else {
        this.scene.time.delayedCall(REVESE_ANIMATION_DELAY, () => {
          SoundPlayer.playerBumpSFX(this.scene);
          this.sprite.anims.reverse();
        });
      }
    } else if (
      this.input.isJustPressedUp &&
      [Dir.Left, Dir.Right].includes(dir)
    ) {
      SoundPlayer.playerTurnSFX(this.scene);
      if (dir === Dir.Left) {
        this.sprite.anims.play(anims.animations.playerLeftUp.key);
      } else {
        this.sprite.anims.playReverse(anims.animations.playerRightUp.key);
      }
      if (
        checkPlayerAction(this.state, PlayerMoves.FaceUp, this.scene.events)
      ) {
        this.state.mutateGameState((state) => {
          getPlayerFromState(state).dir = Dir.Up;
        });
      } else {
        this.scene.time.delayedCall(REVESE_ANIMATION_DELAY, () => {
          SoundPlayer.playerBumpSFX(this.scene);
          this.sprite.anims.reverse();
        });
      }
    } else if (
      this.input.isJustPressedLeft &&
      [Dir.Up, Dir.Down].includes(dir)
    ) {
      SoundPlayer.playerTurnSFX(this.scene);
      if (dir === Dir.Up) {
        this.sprite.anims.playReverse(anims.animations.playerLeftUp.key);
      } else {
        this.sprite.anims.play(anims.animations.playerLeftDown.key);
      }
      if (
        checkPlayerAction(this.state, PlayerMoves.FaceLeft, this.scene.events)
      ) {
        this.state.mutateGameState((state) => {
          getPlayerFromState(state).dir = Dir.Left;
        });
      } else {
        this.scene.time.delayedCall(REVESE_ANIMATION_DELAY, () => {
          SoundPlayer.playerBumpSFX(this.scene);
          this.sprite.anims.reverse();
        });
      }
    } else if (
      this.input.isJustPressedRight &&
      [Dir.Up, Dir.Down].includes(dir)
    ) {
      SoundPlayer.playerTurnSFX(this.scene);
      if (dir === Dir.Up) {
        this.sprite.anims.play(anims.animations.playerRightUp.key);
      } else {
        this.sprite.anims.playReverse(anims.animations.playerRightDown.key);
      }
      if (
        checkPlayerAction(this.state, PlayerMoves.FaceRight, this.scene.events)
      ) {
        this.state.mutateGameState((state) => {
          getPlayerFromState(state).dir = Dir.Right;
        });
      } else {
        this.scene.time.delayedCall(REVESE_ANIMATION_DELAY, () => {
          SoundPlayer.playerBumpSFX(this.scene);
          this.sprite.anims.reverse();
        });
      }
    }
  }
}
